import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { SEO } from "@/components/SEO";
import {
  Shield,
  Target,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Lock,
  Clock,
  Zap,
  Brain,
} from "lucide-react";
import {
  HeroSection,
  LogoCloud,
  StatsCounter,
  BeforeAfterDemo,
  UseCaseCards,
  TestimonialCards,
  FAQSection,
  ROICalculator,
  ScrollAnimatedSection,
} from "@/components/landing";

const features = [
  {
    icon: Target,
    title: "Clarity Scoring",
    description: "Every requirement gets a clarity score so you know what needs attention.",
  },
  {
    icon: Shield,
    title: "Privacy First",
    description: "Temporary mode available. Enterprise supports zero-retention execution.",
  },
  {
    icon: Sparkles,
    title: "Jira Integration",
    description: "Export improved requirements directly to Jira with the reqqy-reviewed label.",
  },
];

const securityFeatures = [
  "Data stored by default for convenience",
  "Temporary mode available",
  "No training on customer data",
  "BYOK and enterprise controls planned / available where true",
];

const howItWorksSteps = [
  {
    step: "01",
    title: "Paste requirements",
    desc: "User stories, PRD excerpts, bullet notes — anything.",
    time: "30 seconds",
  },
  { step: "02", title: "Add context", desc: "Optional: constraints, success metrics, personas.", time: "Optional" },
  { step: "03", title: "Run Reqqy", desc: "Quick or deep analysis — you choose.", time: "1-2 min" },
  { step: "04", title: "Get clarity", desc: "See what's unclear and accept improvements.", time: "Instant" },
];

export default function Landing() {
  return (
    <PublicLayout>
      <SEO
        canonical="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "Reqqy",
          applicationCategory: "BusinessApplication",
          description: "Reqqy is an AI-powered requirements clarity and ambiguity detection tool for product teams.",
          url: "https://reqqy.app",
          operatingSystem: "Web",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        }}
      />

      <HeroSection />
      {/* <LogoCloud /> */}
      {/* <StatsCounter /> */}

      {/* How It Works */}
      <section className="py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-card/30" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[-10%] left-[30%] w-[400px] h-[300px] rounded-full bg-primary/[0.04] blur-[120px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                <Zap className="w-3.5 h-3.5" />
                How it works
              </div>
              <h2 className="text-2xl md:text-3xl font-display font-bold mb-3">Clear requirements in minutes</h2>
              <p className="text-base text-muted-foreground max-w-xl mx-auto">
                Paste what you would normally send to engineering. Reqqy reviews it and tells you what's unclear.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              {howItWorksSteps.map((item, index) => (
                <div key={item.step} className="relative group">
                  <div className="relative glass p-5 rounded-xl border border-border/30 h-full transition-all duration-300 hover:border-primary/20 hover:-translate-y-0.5">
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-primary/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="relative z-10">
                      <div className="text-3xl font-display font-bold text-primary/20 mb-3 group-hover:text-primary/40 transition-colors">
                        {item.step}
                      </div>
                      <h3 className="text-sm font-semibold mb-1.5">{item.title}</h3>
                      <p className="text-xs text-muted-foreground mb-2.5">{item.desc}</p>
                      <div className="inline-flex items-center gap-1 text-[11px] text-primary bg-primary/10 px-2 py-0.5 rounded-md">
                        <Clock className="w-3 h-3" />
                        {item.time}
                      </div>
                    </div>
                  </div>
                  {index < 3 && (
                    <ArrowRight className="hidden md:block absolute top-6 -right-3.5 z-10 w-4 h-4 text-primary/30" />
                  )}
                </div>
              ))}
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      <ScrollAnimatedSection>
        <BeforeAfterDemo />
      </ScrollAnimatedSection>

      <ROICalculator />

      <ScrollAnimatedSection>
        <UseCaseCards />
      </ScrollAnimatedSection>

      {/* <ScrollAnimatedSection>
        <TestimonialCards />
      </ScrollAnimatedSection> */}

      {/* Features Section */}
      <section id="features" className="py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-card/20" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[20%] right-[15%] w-[400px] h-[400px] rounded-full bg-primary/[0.04] blur-[140px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                Features
              </div>
              <h2 className="text-2xl md:text-3xl font-display font-bold mb-3">Built for product teams</h2>
              <p className="text-base text-muted-foreground max-w-xl mx-auto">
                Reqqy helps you delegate the tedious work of reviewing requirements while staying in control.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className="group relative p-5 md:p-6 rounded-xl glass border border-border/30 animate-fade-in transition-all duration-300 hover:border-primary/20 hover:-translate-y-0.5"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-primary/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10">
                    <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center mb-4">
                      <feature.icon className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <h3 className="text-base font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      {/* Privacy-first by design */}
      <section className="py-14 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-card/30" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/40 to-transparent" />
        <div className="absolute top-[10%] left-[20%] w-[500px] h-[400px] rounded-full bg-primary/[0.03] blur-[140px]" />

        <div className="container relative z-10 px-4 md:px-6">
          <ScrollAnimatedSection>
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4">
                  <Shield className="w-3.5 h-3.5" />
                  Privacy-first by design
                </div>
                <h2 className="text-2xl md:text-3xl font-display font-bold mb-4">Security and privacy built in</h2>
                <p className="text-base text-muted-foreground mb-6">
                  Reqqy is built for sensitive product work. We support standard and temporary processing modes, do not train on customer data, and are designing toward enterprise-grade privacy controls.
                </p>
                <ul className="space-y-2">
                  {securityFeatures.map((feature) => (
                    <li key={feature} className="flex items-center gap-2.5 p-1.5 -ml-1.5 rounded-md">
                      <CheckCircle2 className="w-4 h-4 text-reqqy-success flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Button asChild size="sm" className="gradient-primary text-primary-foreground hover:opacity-90">
                    <Link to="/pricing">
                      View Enterprise Plans
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="border-border/60 hover:border-primary/30">
                    <Link to="/contact-sales">Book a Demo</Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-xl glass p-6 relative overflow-hidden border border-border/30">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[250px] h-[150px] rounded-full bg-primary/[0.05] blur-[60px]" />
                  <div className="relative z-10 flex flex-col items-center justify-center gap-6 py-4">
                    <div className="w-16 h-16 rounded-xl gradient-primary flex items-center justify-center glow-primary">
                      <Lock className="w-8 h-8 text-primary-foreground" />
                    </div>

                    <div className="text-center max-w-xs">
                      <div className="text-2xl font-display font-bold mb-1">
                        Privacy-First
                      </div>
                      <div className="text-muted-foreground text-sm leading-relaxed">
                        Reqqy is built for sensitive product work, with support for standard and temporary workflows.
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
                      <div className="text-center rounded-lg bg-background/40 border border-border/30 px-4 py-3">
                        <div className="text-lg font-bold">Zero</div>
                        <div className="text-[11px] text-muted-foreground">Training on data</div>
                      </div>
                      <div className="text-center rounded-lg bg-background/40 border border-border/30 px-4 py-3">
                        <div className="text-lg font-bold">Temporary</div>
                        <div className="text-[11px] text-muted-foreground">Processing mode</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap justify-center gap-2 text-[11px] text-muted-foreground">
                      {["Private by default", "Versioned history", "Enterprise controls evolving"].map((badge) => (
                        <span
                          key={badge}
                          className="px-2.5 py-1 rounded-md bg-background/50 border border-border/30 font-medium"
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollAnimatedSection>
        </div>
      </section>

      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-card/50" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/[0.10] rounded-full blur-[140px]" />

        <div className="absolute inset-0 opacity-[0.02] mix-blend-overlay" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }} />

        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <div className="container relative z-10 px-4 md:px-6 text-center">
          <ScrollAnimatedSection>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass border border-primary/20 text-xs mb-8">
              <Brain className="w-3.5 h-3.5 text-primary" />
              <span className="text-muted-foreground">Requirements Intelligence for Product Teams</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 leading-[1.1] tracking-[-0.02em]">
              Ready for <span className="text-gradient">clearer</span> requirements?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-lg mx-auto mb-8 leading-relaxed">
              Stop sending ambiguous requirements to engineering. Let Reqqy help you get it right the first time.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                asChild
                size="lg"
                className="relative group h-11 px-7 text-sm font-semibold gradient-primary text-primary-foreground overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_-8px_hsl(var(--primary)/0.5)]"
              >
                <Link to="/dashboard/new-run?mode=review">
                  <span className="relative z-10 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Start reviewing — free
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-11 px-7 text-sm border-border/60 hover:border-primary/40">
                <Link to="/contact-sales">Talk to Sales</Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground/70 mt-4 tracking-wide">
              No credit card required · 10 free credits · Setup in 30 seconds
            </p>
          </ScrollAnimatedSection>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background to-transparent" />
      </section>
    </PublicLayout>
  );
}
